/**
 * Interruttori degli alert automatici — configurazione condivisa (INB-24).
 *
 * Ogni famiglia di alert ha una sua chiave in `configurations.settings`: la
 * scrive il pannello "Notifiche automatiche" del profilo web, la legge il cron
 * dell'API. Le chiavi vivono qui per lo stesso motivo di
 * [[weekly-summary]]: una divergenza fra le due superfici non produce alcun
 * errore visibile — l'alert semplicemente smette di partire, o peggio continua
 * a partire dopo che l'amministratore lo ha spento.
 *
 * `anomalyDetection` non è un refuso: è la chiave già salvata dalla levetta
 * "Alert anomalie POD" del profilo, che finora non aveva alcun produttore.
 * Rinominarla avrebbe riacceso l'alert a chi lo aveva spento.
 */

/** Chiave in `configurations.settings`: anomalie sui POD della configurazione. */
export const ALERT_POD_KEY = 'anomalyDetection';

/**
 * Chiave in `configurations.settings`: irregolarità nella serie dei consumi.
 *
 * Levetta separata da `anomalyDetection` e non un suo riuso, al contrario di
 * quanto fatto per l'ingest muto (INB-31): lì l'amministratore vedeva la stessa
 * famiglia — la sorgente dati non risponde — mentre qui il dato arriva
 * regolarmente ed è il consumo a essere strano. Sono due destinatari diversi
 * (chi sistema l'anagrafica e il gateway, chi telefona al socio) e chi spegne il
 * rumore dell'uno non vuole necessariamente spegnere l'altro.
 */
export const ALERT_CONSUMI_KEY = 'alertConsumiAnomali';

/** Chiave in `configurations.settings`: degrado dell'accuratezza del modello predittivo. */
export const ALERT_ML_ACCURACY_KEY = 'alertMlAccuracy';

/**
 * Chiave in `configurations.settings`: soglia di accuratezza sotto la quale il
 * cron notifica il degrado, in percentuale. Numerica, non booleana — è l'unica
 * impostazione di questa famiglia che porta un valore invece di un interruttore.
 */
export const ML_ACCURACY_THRESHOLD_KEY = 'mlAccuracyThreshold';

/** Soglia usata quando l'amministratore non ne ha configurata una. */
export const ML_ACCURACY_THRESHOLD_DEFAULT = 80;

/**
 * La soglia configurata, oppure il default se manca o non è un numero sensato.
 *
 * Fuori da (0, 100) la soglia non ha significato: a 0 non scatterebbe mai, a 100
 * scatterebbe sempre. Un valore corrotto torna al default invece di spegnere in
 * silenzio l'alert o di trasformarlo in rumore quotidiano.
 */
export function mlAccuracyThreshold(settings: Record<string, unknown>): number {
	const raw = settings[ML_ACCURACY_THRESHOLD_KEY];
	const value = typeof raw === 'number' ? raw : Number(raw);
	if (!Number.isFinite(value) || value <= 0 || value >= 100) return ML_ACCURACY_THRESHOLD_DEFAULT;
	return value;
}

/**
 * Un alert la cui chiave non è mai stata salvata è **attivo**: il default di
 * ogni tipo è on, e solo un `false` esplicito lo spegne. Vale sia per il cron
 * che per le levette del profilo — se le due leggessero il default in modo
 * diverso, la levetta mostrerebbe uno stato che il cron non rispetta.
 */
export function alertEnabled(settings: Record<string, unknown>, key: string): boolean {
	return settings[key] !== false;
}
